import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";
import { WorkspaceList } from "../workspaces";

type JobAction = "google_auth:save-objects" | "bq:export";

interface DataQueryParams {
    mode: "base" | "activations";
    output?: "table" | "charts";
    workspace_id: number;
    group_by?: string[];
    fields?: string[];
    page_size?: number;
    page?: number;
    comparison?: string;
    start: string;
    end: string;
    interval?: string;
    key?: string;
    devices?: string[];
    countries?: string[];
    search_terms?: string[];
    platforms?: string[];
    start_compare?: string;
    end_compare?: string;
    filters?: any;
    extra_options?: any;
    order_by?: [string, string][];
    _export?: "full" | "page";
    tags?: string[];
}

export interface KPI {
    id: string;
    en: string;
    fr: string;
    color: string;
    order: number;
    "color:lighter": string;
    "color:darker": string;
    "color:dark": string;
    "color:light": string;
    "color:contrast_text": string;
    format: [string, string];
    show: boolean;
    viz: "base" | "activation" | "both" | "dimension" | null;
    invert?: boolean;
}

export interface Notification {
    workspace_id?: number;
    workspace?: WorkspaceList;
    id: string;
    title: string;
    content: string;
    created_at: string;
    status: "unread" | "read" | "dismissed" | "archived";
    type?: "workspace_access" | "onboarding" | null;
}

export interface Job {
    id: string;
    workspace_id: number;
    action: JobAction;
    name: string;
    status: "pending" | "running" | "completed" | "failed" | "created";
    created_at: string;
    updated_at: string;
}

type KPIs = KPI[];

interface DataQueryResponse {
    [prop: string]: any;
    query_job_id?: string;
    data?: any[];
    day?: string;
    month?: string;
    year?: string;
    week?: string;
    keyword?: string;
}

const appendArrayParams = (queryParams: URLSearchParams, paramName: string, values?: string[] | string | number, acc: any = null) => {
    if (Array.isArray(values)) {
        if (acc && values.length > 15) {
            acc[paramName] = values;
        }
        else {
            values.filter((value) => !!value).forEach((value) => queryParams.append(paramName, value));
        }
    }
    else if (!!values) {
        queryParams.set(paramName, values.toString());
    }
};

export const makeTrafficQuery = ({ mode, output, filters, extra_options, order_by, _export, ...params }: DataQueryParams) => {
    const queryParams = new URLSearchParams();
    const url = `/details/${output}/${mode}`;
    let method = "GET";
    let body: any = { filters };

    Object.entries(params).forEach(([key, value]) => appendArrayParams(queryParams, key, value, body));

    if (order_by) {
        body.order_by = order_by;
    }

    if (extra_options) {
        body.extra_options = extra_options;
    }

    if (Object.keys(body).filter((key) => !!body[key]).length > 0) {
        method = "POST";
    }
    else {
        body = undefined;
    }

    let exportUrl = "";

    if (_export) {
        exportUrl = `/export/${_export}`;
    }

    return {
        url: `${url}${exportUrl}?${queryParams.toString()}`,
        method: method,
        body,
    }
}

export const dataApi = createApi({
    reducerPath: "dataApi",
    baseQuery: fetchBaseQueryCSRF({
        baseUrl: process.env.CROSS_API_URL + "/traffic",
    }),
    endpoints: (builder) => ({
        kpis: builder.query<KPIs, void>({
            query: () => ({
                url: "/kpis",
                method: "GET",
            }),
        }),
        data: builder.query<DataQueryResponse, DataQueryParams>({
            query: makeTrafficQuery,
        }),
        export: builder.mutation<void, { queryId: string, name: string, columns: string[] }>({
            query: ({ queryId, name, columns }) => ({
                url: `/export/${queryId}?name=${name}`,
                method: "POST",
                params: {
                    columns: columns,
                },
            }),
        }),
        exportStatus: builder.query<{ status: "pending" | "running" | "completed" | "failed" | "created" }, string>({
            query: (queryID) => ({
                url: `/export/${queryID}`,
                method: "GET",
            }),
        }),
        exportJobs: builder.query<{ jobs: Job[] }, void>({
            query: () => ({
                url: "/export/jobs",
                method: "GET",
            }),
        }),
        notifications: builder.query<{ notifications: Notification[] }, void>({
            query: () => ({
                url: "/notification",
                method: "GET",
            }),
        }),
        updateNotificationStatus: builder.mutation<void, { notificationId: string, status: "read" | "dismissed" | "archived" }>({
            query: ({ notificationId, status }) => ({
                url: `/notification/${notificationId}/${status}`,
                method: "POST",
            }),
        }),
    }),
});

export const { useDataQuery, useKpisQuery, useExportMutation, useExportStatusQuery, useExportJobsQuery, useNotificationsQuery, useUpdateNotificationStatusMutation } = dataApi;